import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const contactFormUrl = 'https://readdy.ai/api/form/d9qsm54nlsngrm4lm59g';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const aboutFaqs = [
    {
      q: '黑料中文网是什么时候成立的？',
      a: '黑料中文网成立于2024年，由一群资深的娱乐记者和互联网从业者共同创办。我们的使命是为公众提供一个真实、深度、及时的娱乐资讯平台，打破娱乐圈的信息壁垒。',
    },
    {
      q: '平台的编辑团队有多大？',
      a: '我们拥有超过30人的专业编辑团队，包括一线娱乐记者、资深编辑、事实核查员和数据分析师。此外还有超过100位合作撰稿人和特约记者分布在全国各地。',
    },
    {
      q: '如何保证内容的独家性？',
      a: '我们通过多条途径保证内容的独家性：自建记者团队深入一线采访、维护广泛的业内人士爆料网络、运用数据分析工具挖掘公开信息中的独家线索、以及对用户提供的爆料进行专业验证和深度加工。',
    },
    {
      q: '网站的商业模式是什么？',
      a: '黑料中文网目前主要通过品牌广告合作和会员增值服务获取收入。我们坚持内容免费开放，让所有读者都能平等获取信息。同时我们严格保持编辑独立，广告收入不影响内容方向。',
    },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料中文网',
    url: 'https://fuxiaoxiao.cn',
    description: '专注于娱乐圈黑料、明星爆料、网红内幕的中文资讯平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@fuxiaoxiao.cn',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title="关于我们 - 黑料中文网"
        description="黑料中文网是专注于娱乐圈黑料、明星爆料、网红内幕的中文资讯平台。了解我们的品牌故事、编辑团队和联系方式。"
        keywords="黑料中文网,关于我们,品牌介绍,联系方式,娱乐爆料平台"
        canonicalUrl="https://fuxiaoxiao.cn/about"
        schema={orgSchema}
      />

      <main className="pt-20 pb-14 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于黑料中文网
              </h1>
              <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                黑料中文网是专注于娱乐圈黑料、明星爆料、网红内幕的中文资讯平台。我们致力于为读者提供真实、深度、独家的娱乐资讯，揭秘娱乐圈不为人知的真相。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打破娱乐圈的信息不对称，让公众了解明星艺人、娱乐公司和行业运作的真实面貌。我们相信，阳光是最好的防腐剂，信息透明有助于推动娱乐行业的健康发展。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的原则</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  真实、客观、负责。我们坚持每一条爆料在发布前经过多重核实，确保信息来源可靠。对于无法完全证实的信息，我们会明确标注。我们拒绝发布未经证实的恶意谣言和人身攻击内容。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-team-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">编辑团队</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们拥有一支超过30人的专业编辑团队，包括多位曾在主流媒体工作超过10年的资深娱乐记者。每位编辑都经过严格的职业伦理培训，确保报道的专业性和公正性。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-bar-chart-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">平台数据</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  自成立以来，黑料中文网已累计发布超过12,580篇独家爆料内容，月活跃读者超过860万。我们的内容被超过200家主流媒体转载引用，已成为中文互联网最具影响力的娱乐爆料平台之一。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              读者评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '张先生', role: '资深娱乐记者', text: '黑料中文网的内容质量在业内是公认的。他们的爆料不仅速度快，而且信息核实做得非常到位，是我每天必看的资讯平台。', rating: 5 },
                { name: '李女士', role: '娱乐行业从业者', text: '作为一个在娱乐圈工作的人，我可以负责任地说，黑料中文网的很多报道都触及到了行业的真实痛点。他们揭露的很多问题确实存在。', rating: 5 },
                { name: '王同学', role: '传媒专业研究生', text: '我把黑料中文网作为研究中国娱乐产业的案例素材。他们的深度报道为学术研究提供了宝贵的第一手资料。', rating: 4 },
                { name: '赵先生', role: '忠实读者', text: '关注黑料中文网快两年了，几乎每天都会刷一刷。他们的内容让我对娱乐圈有了全新的认识，不再是表面上的光鲜亮丽。', rating: 5 },
                { name: '陈女士', role: '自媒体博主', text: '黑料中文网的很多内容给了我创作灵感。他们的独家爆料经常成为我视频的素材来源，读者反响非常好。', rating: 4 },
                { name: '刘先生', role: '律师', text: '从法律从业者的角度看，黑料中文网在报道尺度的把握上做得相当专业。既揭露了问题，又没有越界侵犯个人合法权益。', rating: 5 },
              ].map((review, index) => (
                <div key={index} className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-medium">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {aboutFaqs.map((faq, index) => (
                <div key={index} className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
                      activeFaq === index ? 'bg-primary-100 text-primary-600 rotate-45' : 'bg-background-200 text-foreground-400'
                    }`}>
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${activeFaq === index ? 'max-h-96 pb-5 px-5' : 'max-h-0'}`}>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@fuxiaoxiao.cn</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h3>
                  <p className="text-sm text-foreground-500">黑料中文网</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-weibo-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">官方微博</h3>
                  <p className="text-sm text-foreground-500">@黑料中文网</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                  发送消息
                </h3>
                {formState === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-800 font-medium mb-2">消息已发送！</p>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-5">
                    <div className="anti-spam-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-2">
                        <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">姓名 *</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">邮箱 *</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">主题 *</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">消息内容 *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                      <span className="text-xs text-foreground-400">最多500个字符</span>
                    </div>

                    {formState === 'error' && formError && (
                      <div className="p-4 rounded-lg bg-primary-50 text-primary-700 text-sm">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      {formState === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}